import React, { Component, MouseEvent } from 'react';
import type { RenderPropChildren } from 'types/children';

import { getInitialState, importImage } from './Painter.helper';

export interface PainterChildrenProps {
  canvas: React.JSX.Element;
  save: () => void;
  setColor: (color: string) => void;
  setLineCap: (type: LineCap) => void;
  setLineJoin: (type: LineJoin) => void;
  setLineWidth: (width: number) => void;
}

export interface IPainterProps {
  height?: number;
  image?: string;
  initialColor?: string;
  initialLineCap?: LineCap;
  initialLineJoin?: LineJoin;
  initialLineWidth?: number;
  onMouseDown?: (event: MouseEvent<HTMLCanvasElement>) => void;
  onMouseMove?: (event: MouseEvent<HTMLCanvasElement>) => void;
  onMouseUp?: (event: MouseEvent<HTMLCanvasElement>) => void;
  onSave?: (canvas: HTMLCanvasElement) => void;
  width?: number;
}

export interface IPainterState {
  canvasHeight: number;
  canvasWidth: number;
  color: string;
  isDrawing: boolean;
  lineCap: LineCap;
  lineJoin: LineJoin;
  lineWidth: number;
}

export interface IPoint {
  x: number;
  y: number;
}

export type CanvasDimensions = [number, number, number];
export type LineCap = 'round' | 'butt' | 'square';
export type LineJoin = 'round' | 'bevel' | 'miter';

export class Painter extends Component<RenderPropChildren<PainterChildrenProps> & IPainterProps, IPainterState> {
  public canvasRef: Nullable<HTMLCanvasElement> = null;
  public ctx: Nullable<CanvasRenderingContext2D> = null;
  public lastX = 0;
  public lastY = 0;
  public scalingFactor = 1;

  public state: IPainterState = {
    ...getInitialState(this.props),
  };

  public static getDerivedStateFromProps({ initialColor, width }: IPainterProps): Nullable<Partial<IPainterState>> {
    return {
      canvasWidth: width,
      color: initialColor,
    };
  }

  public render() {
    const { children } = this.props;
    const canvasNode = (
      <canvas
        onMouseDown={this.onMouseDown}
        onMouseMove={this.onMouseMove}
        onMouseUp={this.onMouseUp}
        ref={element => {
          this.canvasRef = element;
        }}
        style={{
          cursor: 'crosshair',
          height: this.state.canvasHeight,
          width: this.state.canvasWidth,
        }}
      />
    );

    return typeof children === 'function'
      ? children({
          canvas: canvasNode,
          save: this.onSave,
          setColor: this.setColor,
          setLineCap: this.setLineCap,
          setLineJoin: this.setLineJoin,
          setLineWidth: this.setLineWidth,
        })
      : canvasNode;
  }

  public componentDidMount() {
    const { width, height, image } = this.props;

    document.body.style.touchAction = 'none';

    if (!image) {
      return this.initializeCanvas(height, width);
    }

    importImage(image)
      .then(importedImage => {
        this.initializeCanvas(height, width, importedImage.height, importedImage.width);

        if (this.ctx) {
          this.ctx.drawImage(importedImage.image, 0, 0, importedImage.width, importedImage.height);
        }
      })
      .catch(_err => {
        this.initializeCanvas(height, width);
      });
  }

  public componentWillUnmount() {
    document.body.style.touchAction = 'initial';
  }

  private extractOffSetFromEvent = (event: MouseEvent<HTMLCanvasElement>): IPoint => {
    const { offsetX: x = 0, offsetY: y = 0 } = event.nativeEvent;

    return {
      x: x * this.scalingFactor,
      y: y * this.scalingFactor,
    };
  };

  private initializeCanvas = (
    height: number = 300,
    width: number = 300,
    imageHeight?: number,
    imageWidth?: number,
  ): void => {
    if (!this.canvasRef) {
      return;
    }

    if (imageHeight && imageWidth) {
      const [canvasWidth, canvasHeight, scalingRatio] = this.getCanvasDimensions(width, imageHeight, imageWidth);

      this.canvasRef.width = imageWidth;
      this.canvasRef.height = imageHeight;
      this.scalingFactor = 1 / scalingRatio;

      this.setCanvasSize(canvasHeight, canvasWidth);
    } else {
      this.canvasRef.height = height;
      this.canvasRef.width = width;

      this.setCanvasSize(height, width);
    }

    this.createContext();
  };

  private createContext = (): void => {
    const { color, lineWidth, lineJoin, lineCap } = this.state;

    this.ctx = this.canvasRef && this.canvasRef.getContext('2d');

    if (!this.ctx) {
      return;
    }

    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth * this.scalingFactor;
    this.ctx.lineJoin = lineJoin;
    this.ctx.lineCap = lineCap;
  };

  private disableDrawing = (): void => {
    this.setState({
      isDrawing: false,
    });
  };

  private drawLine = (event: MouseEvent<HTMLCanvasElement>): void => {
    const { ctx, lastX, lastY, state } = this;
    const { color, lineWidth, lineCap, lineJoin } = state;
    const { x, y } = this.extractOffSetFromEvent(event);

    if (!this.state.isDrawing || !ctx) {
      return;
    }

    ctx.lineCap = lineCap;
    ctx.lineJoin = lineJoin;
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth * this.scalingFactor;

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();

    this.lastX = x;
    this.lastY = y;
  };

  private enableDrawing = (): void => {
    this.setState({
      isDrawing: true,
    });
  };

  private getCanvasDimensions = (width: number, imageHeight: number, imageWidth: number): CanvasDimensions => {
    if (imageWidth <= width) {
      return [imageWidth, imageHeight, 1];
    }

    const scalingRatio = width / imageWidth;

    return [width, scalingRatio * imageHeight, scalingRatio];
  };

  private onMouseDown = (event: MouseEvent<HTMLCanvasElement>): void => {
    const { x, y } = this.extractOffSetFromEvent(event);

    this.saveLastPoints(x, y);
    this.enableDrawing();

    if (typeof this.props.onMouseDown === 'function') {
      this.props.onMouseDown(event);
    }
  };

  private onMouseMove = (event: MouseEvent<HTMLCanvasElement>): void => {
    this.drawLine(event);

    if (typeof this.props.onMouseMove === 'function') {
      this.props.onMouseMove(event);
    }
  };

  private onMouseUp = (event: MouseEvent<HTMLCanvasElement>): void => {
    this.disableDrawing();

    if (typeof this.props.onMouseUp === 'function') {
      this.props.onMouseUp(event);
    }
  };

  private onSave = (): void => {
    const { canvasRef, props } = this;

    if (canvasRef && typeof props.onSave === 'function') {
      props.onSave(canvasRef);
    }
  };

  private saveLastPoints = (x: number, y: number): void => {
    this.lastX = x;
    this.lastY = y;
  };

  private setCanvasSize = (height: number, width: number): void => {
    this.setState({
      canvasHeight: height,
      canvasWidth: width,
    });
  };

  private setColor = (color: string): void => {
    this.setState({
      color,
    });
  };

  private setLineCap = (lineCap: 'round' | 'butt' | 'square'): void => {
    this.setState({
      lineCap,
    });
  };

  private setLineJoin = (lineJoin: LineJoin): void => {
    this.setState({
      lineJoin,
    });
  };

  private setLineWidth = (lineWidth: number): void => {
    this.setState({
      lineWidth,
    });
  };
}
